import { useQuery } from '@tanstack/react-query';

import { fetchPremiumTeamSections } from '@api-service/content';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0User } from '@hooks/useAuth0User';
import { hydrateArticleBlocks } from '@lib/article-blocks/hydrate';
import type { ArticleBlockData } from '@lib/article-blocks/types';
import { flattenNotes } from '@lib/predicted-lineups/compose';
import type {
  SanityPredictedLineupTeamSection,
  SanityPredictedLineupsPage
} from '@lib/sanity';
import { hasSufficientPrivileges, ROLE_LEVELS } from '@lib/utils';

const PREMIUM_ACCESS_LEVEL = 'premium';
// Matches the API's cache and the page's ISR window.
const PREMIUM_SECTIONS_STALE_TIME_IN_MS = 10 * 60 * 1000;

export type PremiumTeamSectionsState =
  | { status: 'not-applicable' }
  | { status: 'loading' }
  | { status: 'locked' }
  | { status: 'error'; retry: () => void }
  | {
      status: 'ready';
      sections: SanityPredictedLineupTeamSection[];
      blockData: ArticleBlockData;
    };

type PremiumTeamSectionsResult = {
  sections?: SanityPredictedLineupTeamSection[];
  blockData?: ArticleBlockData;
};

/**
 * Resolves the gated clubs' words for the current visitor, mirroring
 * usePremiumBody: the static page never holds them, an entitled member fetches
 * them from the content API with their token, and everyone else is `locked`.
 * The client-side entitlement check only decides whether to attempt the
 * fetch; the API is the real gate.
 */
export const usePremiumTeamSections = (
  page: SanityPredictedLineupsPage | undefined
): PremiumTeamSectionsState => {
  const { user, isLoading: isUserLoading } = useAuth0User();
  const { accessToken } = useAccessToken();

  const isPremium = page?.accessLevel === PREMIUM_ACCESS_LEVEL;
  const isEntitled = hasSufficientPrivileges(user, ROLE_LEVELS.PRO);
  const isFetchEnabled = isPremium && isEntitled && Boolean(accessToken);

  const { data, isError, isSuccess, refetch } =
    useQuery<PremiumTeamSectionsResult>({
      queryKey: [
        'premium-team-sections',
        page?.gameweek,
        page?._updatedAt,
        user?.sub
      ],
      queryFn: async () => {
        if (!accessToken) return {};
        const sections = await fetchPremiumTeamSections(accessToken);
        if (!sections) return {};
        return {
          sections,
          blockData: await hydrateArticleBlocks(flattenNotes(sections))
        };
      },
      enabled: isFetchEnabled,
      staleTime: PREMIUM_SECTIONS_STALE_TIME_IN_MS,
      refetchOnWindowFocus: false,
      retry: false
    });

  if (!isPremium) return { status: 'not-applicable' };
  if (isUserLoading) return { status: 'loading' };
  if (!isEntitled) return { status: 'locked' };
  if (isError) return { status: 'error', retry: () => void refetch() };
  if (isSuccess) {
    return data.sections && data.blockData
      ? { status: 'ready', sections: data.sections, blockData: data.blockData }
      : { status: 'locked' };
  }
  return { status: 'loading' };
};
